import type { ClaimValueBreakdown } from "@hp/types";
import { formatINR, formatINRRange } from "../../lib/format";

interface ClaimValueBreakdownPanelProps {
  valueLow: number;
  valueHigh: number;
  breakdown: ClaimValueBreakdown | null | undefined;
  compact?: boolean;
}

/** Principal (RoE differential) vs late-payment surcharge (LPS), with total. */
export function ClaimValueBreakdownPanel({
  valueLow,
  valueHigh,
  breakdown,
  compact = false,
}: ClaimValueBreakdownPanelProps) {
  const principalLow = breakdown?.principal_low ?? valueLow;
  const principalHigh = breakdown?.principal_high ?? valueHigh;
  const carryingLow = breakdown?.carrying_cost_low ?? 0;
  const carryingHigh = breakdown?.carrying_cost_high ?? 0;
  const hasCarrying = carryingHigh > 0 || carryingLow > 0;

  if (compact) {
    return (
      <div className="space-y-0.5 text-xs">
        <div className="font-mono tabular-nums text-slate-900">
          Principal: {formatINRRange(principalLow, principalHigh)}
        </div>
        {hasCarrying ? (
          <div className="font-mono tabular-nums text-slate-600">
            LPS: {formatINRRange(carryingLow, carryingHigh)}
          </div>
        ) : null}
        <div className="font-mono tabular-nums font-medium text-slate-900">
          Total: {formatINRRange(valueLow, valueHigh)}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Principal (RoE differential)</p>
        <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-slate-900">
          {formatINRRange(principalLow, principalHigh)}
        </p>
        {breakdown?.share_applied != null ? (
          <p className="mt-1 text-xs text-slate-500">At {(breakdown.share_applied * 100).toFixed(0)}% contracted share</p>
        ) : null}
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Late payment surcharge (LPS)</p>
        <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-slate-900">
          {hasCarrying ? formatINRRange(carryingLow, carryingHigh) : "—"}
        </p>
        {breakdown?.carrying_cost_rate_pct_per_month != null ? (
          <p className="mt-1 text-xs text-slate-500">
            {breakdown.carrying_cost_rate_pct_per_month}%/month · {breakdown.carrying_cost_compounding ?? "simple"}
            {breakdown.carrying_cost_from ? ` · from ${breakdown.carrying_cost_from}` : ""}
          </p>
        ) : null}
      </div>
      <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-brand-700">Total recoverable (high)</p>
        <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-brand-900">
          {formatINR(valueHigh)}
        </p>
        <p className="mt-1 text-xs text-brand-800/80">
          Range {formatINRRange(valueLow, valueHigh)}
        </p>
      </div>
    </div>
  );
}
